import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useState } from 'react';
import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultSAL3050_R01DetailDto, SAL3050_R01DetailDto } from './Dto_SAL3050_R01';
import { useSAL3050_R01Store } from './Store_SAL3050_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'SAL3050_R01-detail-grid-column-state';

interface Props {
    gridRef: React.RefObject<AgGridReact<SAL3050_R01DetailDto> | null>;
}

export const DetailTable_SAL3050_R01 = ({ gridRef }: Props) => {
    const {
        SAL3050_R01DetailList,
        detailColumnState, setDetailColumnState,
        detailScrollTop, setDetailScrollTop,
    } = useSAL3050_R01Store();

    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const {
        getRowId,
        settingRowIndex,
        createRowClassRules,
        paddedGridData,
    } = useGridSettingContent({
        gridRef,
        key: 'valueIndex',
        gridData: SAL3050_R01DetailList,
        defaultGridData: defaultSAL3050_R01DetailDto,
        selectedRow: [],
    });

    const getCellStyle = useCallback((params: { data?: SAL3050_R01DetailDto }, key: string) => onlyTouchGridStyle(key, 'itemCd')(params), []);

    const headerNames: Record<string, string> = {
        rowIndex: '',
        txDate: '날짜',
        itemCd: '품목코드',
        itemNm: '품명',
        qty: '수량',
        unitPrice: '단가',
        discount: '할인',
        salPayAmt: '매출.지불액',
        purColAmt: '매입.수금액',
        balance: '잔액',
        remark: '비고',
    };

    const amtFormatter = (params: { value?: number }) =>
        params.value ? params.value.toLocaleString() : '';

    const baseColumnDefs: ColDef<SAL3050_R01DetailDto>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(),
        {
            headerName: '날짜', colId: 'txDate', field: 'txDate', width: 110,
            cellStyle: (p) => getCellStyle(p, 'txDate'),
            headerClass: aggridHeaderClass,
        },
        {
            headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 130,
            cellStyle: (p) => getCellStyle(p, 'itemCd'),
            headerClass: aggridHeaderClass,
        },
        {
            headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 180,
            cellStyle: (p) => getCellStyle(p, 'itemNm'),
            headerClass: aggridHeaderClass,
        },
        {
            headerName: '수량', colId: 'qty', field: 'qty', width: 80,
            cellStyle: (p) => getCellStyle(p, 'qty'),
            headerClass: aggridHeaderClass,
            comparator: (a, b) => a - b,
            valueFormatter: amtFormatter,
        },
        {
            headerName: '단가', colId: 'unitPrice', field: 'unitPrice', width: 110,
            cellStyle: (p) => getCellStyle(p, 'unitPrice'),
            headerClass: aggridHeaderClass,
            comparator: (a, b) => a - b,
            valueFormatter: amtFormatter,
        },
        {
            headerName: '할인', colId: 'discount', field: 'discount', width: 90,
            cellStyle: (p) => getCellStyle(p, 'discount'),
            headerClass: aggridHeaderClass,
            comparator: (a, b) => a - b,
            valueFormatter: amtFormatter,
        },
        {
            headerName: '매출.지불액', colId: 'salPayAmt', field: 'salPayAmt', width: 120,
            cellStyle: (p) => getCellStyle(p, 'salPayAmt'),
            headerClass: aggridHeaderClass,
            comparator: (a, b) => a - b,
            valueFormatter: amtFormatter,
        },
        {
            headerName: '매입.수금액', colId: 'purColAmt', field: 'purColAmt', width: 120,
            cellStyle: (p) => getCellStyle(p, 'purColAmt'),
            headerClass: aggridHeaderClass,
            comparator: (a, b) => a - b,
            valueFormatter: amtFormatter,
        },
        {
            headerName: '잔액', colId: 'balance', field: 'balance', width: 120,
            cellStyle: (p) => {
                const base = getCellStyle(p, 'balance') ?? {};
                return (p.value ?? 0) < 0 ? { ...base, color: 'red' } : base;
            },
            headerClass: aggridHeaderClass,
            comparator: (a, b) => a - b,
            valueFormatter: amtFormatter,
        },
        {
            headerName: '비고', colId: 'remark', field: 'remark', width: 180,
            cellStyle: (p) => getCellStyle(p, 'remark'),
            headerClass: aggridHeaderClass,
        },
    ], [getCellStyle]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
    , [baseColumnDefs]);

    const columnDefs: ColDef<SAL3050_R01DetailDto>[] = useMemo(() => {
        if (detailColumnState === null) {
            return baseColumnDefs;
        }
        return GridCustomColumnDefs<SAL3050_R01DetailDto>({
            baseColumnDefs,
            columnState: detailColumnState,
            headerNames,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [detailColumnState, settingRowIndex, baseColumnDefs, headerNames]);

    const pinnedBottomRowData = useMemo(() => [{
        itemCd: SAL3050_R01DetailList.length.toString() + "건",
        salPayAmt: SAL3050_R01DetailList.reduce((s, r) => s + (r.salPayAmt ?? 0), 0),
        purColAmt: SAL3050_R01DetailList.reduce((s, r) => s + (r.purColAmt ?? 0), 0),
        balance: SAL3050_R01DetailList[SAL3050_R01DetailList.length - 1]?.balance ?? 0,
    }], [SAL3050_R01DetailList]);

    return (
        <GridContextWrapper<SAL3050_R01DetailDto>
        	enableCellCalculation
            gridRef={gridRef}
            paddedGridData={paddedGridData as SAL3050_R01DetailDto[]}
            initSelected={() => {}}
            defaultRowData={defaultSAL3050_R01DetailDto}
            baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
            defaultColumnState={defaultColumnState}
            setColumnState={setDetailColumnState}
            localStorageKey={COLUMN_STATE_KEY}
            menuItems={undefined}
        >
            <Grid_Touch<SAL3050_R01DetailDto>
                ref={gridRef}
                rowData={paddedGridData as SAL3050_R01DetailDto[]}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowId={getRowId}
                rowClassRules={createRowClassRules<SAL3050_R01DetailDto>({
                    idKey: 'valueIndex',
                    selectedList: [],
                    deletedList: [],
                })}
                onRowClicked={undefined}
                onCellKeyDown={undefined}
                pinnedBottomRowData={pinnedBottomRowData}
                setHoveredRowIndex={setHoveredRowIndex}
                columnStateKey={COLUMN_STATE_KEY}
                setColumnState={setDetailColumnState}
                scrollTop={detailScrollTop}
                setScrollTop={setDetailScrollTop}
            />
        </GridContextWrapper>
    );
};
